/*
 * @providesModule AeropayAPIController
 *
 * This module provides a set of functions to interact with the Aeropay API.
 */

import {NativeModules} from 'react-native';
import NetClient from 'NetClient';
import RESTStore from 'RESTStore';
import {ErrorObjectType, ErrorResponse} from 'NetClientTypes';
import SKZPersonalInfo from 'SKZPersonalInfo';
import {
  AeropayError,
  CreateAeropayBankAccountPayload,
  CreateAeropayBankAccountResponse,
  CreateAeropayUserResponse,
  DeleteAeropayBankAccountPayload,
  GetAerosyncURLResponse,
} from './AeropayController.types';

const SKZAPIManager = NativeModules.SKZAPIManager;

export default class AeropayAPIController {
  static createAeropayUser(userInformation: SKZPersonalInfo): Promise<CreateAeropayUserResponse> {
    return new Promise(
      (
        resolve: (response: CreateAeropayUserResponse) => void,
        reject: (error: AeropayError) => void
      ) => {
        NetClient.postURL(
          '/payment/v1/aeropay-user',
          userInformation,
          SKZAPIManager.OnError.failSilently,
          (error: ErrorResponse<AeropayError | ErrorObjectType> | null, response?: unknown) => {
            // handle custom error response for MFA flow
            if (
              (error?.responseObject as AeropayError)?.code === 'USER_ALREADY_EXISTS_IN_NETWORK'
            ) {
              return resolve({
                shouldShowMFAFlow: true,
                userPersonalInfo: userInformation,
                MFASubtitleText: (error?.responseObject as AeropayError)?.message,
              });
            }

            if (error) {
              console.error(JSON.stringify(error));
              return reject({
                code: (error?.responseObject as AeropayError)?.code,
                message: (error?.responseObject as AeropayError)?.message,
              } as AeropayError);
            }

            if (!error && response) {
              const aeropayResponse = response as SKZPersonalInfo & AeropayError;
              if (
                aeropayResponse.hasOwnProperty('code') &&
                aeropayResponse.code === 'USER_ALREADY_EXISTS_IN_NETWORK'
              ) {
                return resolve({
                  shouldShowMFAFlow: true,
                  userPersonalInfo: userInformation,
                  MFASubtitleText: aeropayResponse.message,
                });
              } else {
                return resolve({
                  shouldShowMFAFlow: false,
                  userPersonalInfo: response as SKZPersonalInfo,
                });
              }
            }
          }
        );
      }
    );
  }

  static createAeropayBankAccount(
    bankLinkPayload: CreateAeropayBankAccountPayload
  ): Promise<CreateAeropayBankAccountResponse> {
    return new Promise(
      (
        resolve: (response: CreateAeropayBankAccountResponse) => void,
        reject: (error: AeropayError) => void
      ) => {
        NetClient.postURL(
          '/payment/v1/bank-accounts',
          bankLinkPayload,
          SKZAPIManager.OnError.failSilently,
          (error: ErrorResponse | null, payload?: unknown) => {
            if (error) {
              console.error(JSON.stringify(error));
              reject({
                code: (error?.responseObject as AeropayError)?.code,
                message: (error?.responseObject as AeropayError)?.message,
              } as AeropayError);
            }

            if (!error && payload) {
              return resolve(payload as CreateAeropayBankAccountResponse);
            }
          }
        );
      }
    );
  }

  static deleteAeropayBankAccount(bankLinkPayload: DeleteAeropayBankAccountPayload): Promise<void> {
    return new Promise((resolve, reject) => {
      NetClient.deleteURL(
        '/payment/v1/bank-accounts',
        bankLinkPayload,
        SKZAPIManager.OnError.failSilently,
        // Ignoring this type error for now for the generic error to pop-up for the user in case of 500 response.
        (error: ErrorResponse<AeropayError> | null, payload?: unknown) => {
          if (error) {
            console.error(JSON.stringify(error));
            reject({
              code: (error?.responseObject as AeropayError)?.code ?? error.code,
              message: (error?.responseObject as AeropayError)?.message,
            } as AeropayError);
          }

          return resolve();
        }
      );
    });
  }

  static getAerosyncURL(): Promise<GetAerosyncURLResponse> {
    return new Promise(
      (
        resolve: (response: GetAerosyncURLResponse) => void,
        reject: (error: AeropayError) => void
      ) => {
        RESTStore.getServerResponse(
          '/payment/v1/aerosync-url',
          null,
          SKZAPIManager.OnError.failSilently,
          (response: GetAerosyncURLResponse, error: ErrorResponse | null) => {
            if (error) {
              reject({
                code: (error?.responseObject as AeropayError)?.code,
                message: (error?.responseObject as AeropayError)?.message,
              } as AeropayError);
            } else {
              resolve(response);
            }
          }
        );
      }
    );
  }
}
